import React from 'react';
import {
    Star,
    Play,
    Clock,
    CheckCircle,
    XCircle,
    Edit,
    Trash2,
    Calendar,
    FileText,
    BarChart3,
    Table,
    Loader2
} from 'lucide-react';
import { SavedReportDTO } from '../../../../../services/claudeQueryService';

interface ReportCardProps {
    report: SavedReportDTO;
    onRun: (id: number) => void;
    onEdit: (report: SavedReportDTO) => void;
    onDelete: (id: number) => void;
    onToggleFavorite: (id: number, isFavorite: boolean) => void;
    isRunning?: boolean;
}

export const ReportCard: React.FC<ReportCardProps> = ({
    report,
    onRun,
    onEdit,
    onDelete,
    onToggleFavorite,
    isRunning = false
}) => {
    const formatDate = (dateStr?: string) => {
        if (!dateStr) return null;
        const d = new Date(dateStr);
        return d.toLocaleDateString('nb-NO', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusIcon = () => {
        if (!report.lastRunStatus) return null;
        switch (report.lastRunStatus) {
            case 'completed':
                return <CheckCircle size={14} className="text-green-500" />;
            case 'failed':
                return <XCircle size={14} className="text-red-500" />;
            default:
                return <Clock size={14} className="text-yellow-500" />;
        }
    };

    const getFormatIcon = () => {
        switch (report.exportFormat) {
            case 'chart':
                return <BarChart3 size={16} className="text-purple-500" />;
            case 'table':
                return <Table size={16} className="text-blue-500" />;
            default:
                return <FileText size={16} className="text-gray-500" />;
        }
    };

    return (
        <div className="bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all p-4">
            {/* Header */}
            <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                    {getFormatIcon()}
                    <h3 className="font-semibold text-gray-800 truncate" title={report.name}>
                        {report.name}
                    </h3>
                </div>
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onToggleFavorite(report.reportId, !report.isFavorite);
                    }}
                    className="flex-shrink-0 ml-2"
                    title={report.isFavorite ? 'Fjern favoritt' : 'Merk som favoritt'}
                >
                    <Star
                        size={16}
                        className={report.isFavorite
                            ? 'text-yellow-400 fill-yellow-400'
                            : 'text-gray-300 hover:text-yellow-400'
                        }
                    />
                </button>
            </div>

            {/* Description */}
            {report.description && (
                <p className="text-sm text-gray-500 mb-3 line-clamp-2">
                    {report.description}
                </p>
            )}

            {/* Category + Schedule badge */}
            <div className="flex items-center gap-2 mb-3 flex-wrap">
                {report.category && (
                    <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">
                        {report.category}
                    </span>
                )}
                {report.scheduleEnabled && (
                    <span className="text-xs bg-green-50 text-green-600 px-2 py-0.5 rounded-full flex items-center gap-1">
                        <Calendar size={10} />
                        Schedulert
                    </span>
                )}
            </div>

            {/* Last run info */}
            {report.lastRunAt && (
                <div className="text-xs text-gray-400 flex items-center gap-1 mb-3">
                    {getStatusIcon()}
                    <span>Siste kjøring: {formatDate(report.lastRunAt)}</span>
                    {report.lastRunRowCount != null && (
                        <span className="text-gray-300">({report.lastRunRowCount} rader)</span>
                    )}
                </div>
            )}

            {/* Actions */}
            <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                <button
                    onClick={() => onRun(report.reportId)}
                    disabled={isRunning}
                    className="flex items-center gap-1 px-3 py-1.5 bg-blue-500 text-white rounded-md
                             hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium transition-colors"
                >
                    {isRunning ? (
                        <Loader2 size={12} className="animate-spin" />
                    ) : (
                        <Play size={12} />
                    )}
                    {isRunning ? 'Kjører...' : 'Kjør nå'}
                </button>
                <button
                    onClick={() => onEdit(report)}
                    className="p-1.5 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded transition-colors"
                    title="Rediger"
                >
                    <Edit size={14} />
                </button>
                <button
                    onClick={() => onDelete(report.reportId)}
                    className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                    title="Slett"
                >
                    <Trash2 size={14} />
                </button>
            </div>
        </div>
    );
};
